<template>
  <IndicatorCard
    :loading="loading"
    :has-data="equips.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum equipamento encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row class="ma-0" justify="center">
      <v-col cols="12">
        <BaseEChart :option="chartOption" :loading="loading" height="320px" />

        <div>
          <div class="hidden-md-and-up">
            <v-data-table
              :loading="loading"
              :headers="headerMobile"
              :items="equips"
              class="main-table"
              hide-default-footer
              show-expand
              item-key="standardEquipmentName"
              :single-expand="false"
              :items-per-page="10"
              :header-props="headerProps"
              :mobile-breakpoint="330"
              :expanded.sync="expanded"
              @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
            >
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length">
                  <v-col class="ma-0 pa-0" cols="12">
                    <p>
                      <strong>{{ $t('Erro operacional') }}:</strong>
                      {{ item.quantityOperationalError }}
                    </p>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <p>
                      <strong>{{ $t('Abuso na utilização') }}:</strong>
                      {{ item.quantityAbuseInUse }}
                    </p>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <p>
                      <strong>{{ $t('Dano acidental') }}:</strong>
                      {{ item.quantityAccidentalDamage }}
                    </p>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <p>
                      <strong>{{ $t('Falha intrínseca') }}:</strong>
                      {{ item.quantityIntrinsicFailure }}
                    </p>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <p>
                      <strong>{{ $t('Não aplicável') }}:</strong>
                      {{ item.quantityNotApplicable }}
                    </p>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <p>
                      <strong>{{ $t('Falha infraestrutura') }}:</strong>
                      {{ item.quantityInfrastructureFailure }}
                    </p>
                  </v-col>
                </td>
              </template>
            </v-data-table>
          </div>

          <div class="hidden-md-and-down">
            <v-data-table
              :loading="loading"
              :headers="header"
              :items="equips"
              class="main-table"
              hide-default-footer
              hide-default-header
              :header-props="headerProps"
              :items-per-page="10"
              :mobile-breakpoint="0"
            >
              <template #header="{ props, on }">
                <thead>
                  <tr>
                    <th
                      class="text-center"
                      v-for="header in props.headers"
                      :key="header.value"
                    >
                      <v-simple-checkbox
                        v-if="header.value === 'data-table-select'"
                        v-model="props.everyItem"
                        :indeterminate="props.someItems && !props.everyItem"
                        color="purple"
                        @input="on['toggle-select-all']"
                      ></v-simple-checkbox>

                      <div>
                        <v-hover v-slot="{ hover }">
                          <div>
                            <span>{{ $t(header.text) }}</span>
                            <v-icon
                              v-if="
                                hover &&
                                header.value != 'data-table-select' &&
                                header.value != 'actions'
                              "
                              color="white"
                              class="mx-2"
                              @click="on.sort(header.value)"
                            >
                              {{
                                props.options.sortBy.includes(header.value)
                                  ? `mdi-chevron-down`
                                  : `mdi-chevron-up`
                              }}
                            </v-icon>
                          </div>
                        </v-hover>
                      </div>
                    </th>
                  </tr>
                </thead>
              </template>
            </v-data-table>
          </div>
        </div>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="
              $t('Digite sua análise crítica para o indicador abaixo')
            "
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          ></tiptap-vuetify>
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import { buildHorizontalBarOption } from '../../shared/chartConfigs'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  async created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.filter.startDate = lastMonthStart
    this.filter.endDate = lastMonthEnd
    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  data: () => ({
    excelLoading: false,
    extensions: [
      History, Blockquote, Link, Underline, Strike, Italic, ListItem,
      BulletList, OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold, Code, HorizontalRule, Paragraph, HardBreak,
    ],
    loading: true,
    startDate: null,
    endDate: null,
    expanded: [],
    equipmentName: [],
    quantityOperationalError: [],
    quantityAbuseInUse: [],
    quantityAccidentalDamage: [],
    quantityIntrinsicFailure: [],
    quantityNotApplicable: [],
    quantityInfrastructureFailure: [],
    equips: [],
    filter: {
      page: 1,
      pageSize: 7,
      review: '',
    },
    page: 1,
    totalPages: 1,
    total: 0,
    header: [
      { text: '', align: 'left', value: '' },
      { text: 'Equipamento Padrão', align: 'left', value: 'standardEquipmentName' },
      { text: 'Total', align: 'center', value: 'totalFailures' },
      { text: 'Erro Operacional', align: 'center', value: 'quantityOperationalError' },
      { text: 'Abuso na Utilização', align: 'center', value: 'quantityAbuseInUse' },
      { text: 'Dano Acidental', align: 'center', value: 'quantityAccidentalDamage' },
      { text: 'Falha Intríseca', align: 'center', value: 'quantityIntrinsicFailure' },
      { text: 'Falha Infraestrutura', align: 'center', value: 'quantityInfrastructureFailure' },
      { text: 'Não Aplicável', align: 'center', value: 'quantityNotApplicable' },
    ],
    headerMobile: [
      { text: 'Equipamento Padrão', align: 'left', value: 'name' },
      { text: 'Total', align: 'center', value: 'total' },
    ],
    headerProps: { sortByText: 'Ordenar por' },
    apiService: new ApiService(),
  }),

  computed: {
    indicator() {
      return getIndicatorById('top_five_failures') || {}
    },
    chartOption() {
      return buildHorizontalBarOption({
        categories: this.equipmentName,
        series: [
          { name: this.$t('Erro operacional'), data: this.quantityOperationalError, color: '#426483' },
          { name: this.$t('Abuso na utilização'), data: this.quantityAbuseInUse, color: '#BBCF20' },
          { name: this.$t('Dano acidental'), data: this.quantityAccidentalDamage, color: '#0F3565' },
          { name: this.$t('Falha intrínseca'), data: this.quantityIntrinsicFailure, color: '#4c6d97' },
          { name: this.$t('Não aplicável'), data: this.quantityNotApplicable, color: '#C4E8F2' },
          { name: this.$t('Falha infraestrutura'), data: this.quantityInfrastructureFailure, color: '#ED8990' },
        ],
        showLegend: true,
      })
    },
  },

  methods: {
    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('top_five_failures').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    extractChartData(response) {
      this.total = response.content.pager.totalItems
      this.totalPages = response.content.pager.totalPages
      this.equipmentName = []
      this.quantityAbuseInUse = []
      this.quantityAccidentalDamage = []
      this.quantityInfrastructureFailure = []
      this.quantityIntrinsicFailure = []
      this.quantityNotApplicable = []
      this.quantityOperationalError = []
      this.equips = response.content.objects

      response.content.objects.map((obj) => {
        this.equipmentName.push(obj.standardEquipmentName)
        this.quantityAbuseInUse.push(obj.quantityAbuseInUse)
        this.quantityAccidentalDamage.push(obj.quantityAccidentalDamage)
        this.quantityInfrastructureFailure.push(obj.quantityInfrastructureFailure)
        this.quantityIntrinsicFailure.push(obj.quantityIntrinsicFailure)
        this.quantityNotApplicable.push(obj.quantityNotApplicable)
        this.quantityOperationalError.push(obj.quantityOperationalError)
      })
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          this.extractChartData(JSON.parse(this.filterProp.data))
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.pageSize = 7
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/topFiveFailures',
          this.filter
        )
        this.extractChartData(response)
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    page() {
      this.filter.page = this.page
      this.list()
    },
    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
